import React, { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { WakeLockButton } from './GameRoom';
import MobileControls, { lockPlayViewport } from './NetplayRoom.mobile';
import NetplayNamedSavesPanel from './NetplayNamedSavesPanel';
import HostOverride from './NetplayRoom.host-override.jsx';
import { controllerAssignments, personalScreenAspect, playerSeats, seatDescription } from './netplayRoomUi';
import './NetplayRoom.css';
const SERVER = process.env.REACT_APP_SERVER_URL || '';
const KEYS = { ArrowUp: 4, ArrowDown: 5, ArrowLeft: 6, ArrowRight: 7, KeyZ: 0, KeyX: 8, KeyA: 1, KeyS: 9, ShiftLeft: 2, Enter: 3, KeyQ: 10, KeyW: 11 };
const B = { padding: '8px 12px', borderRadius: 7, border: '1px solid #555', background: '#26232c', color: '#fff', cursor: 'pointer', maxWidth: '100%', boxSizing: 'border-box' };
function token(basePath, fresh = false) {
  const key = basePath === '/netplay' ? 'tavern-netplay-session' : 'tavern-netplay-session-side';
  let t; try { if (!fresh) t = sessionStorage.getItem(key); } catch {}
  if (!/^[a-f0-9]{64}$/.test(t || '')) {
    t = [...crypto.getRandomValues(new Uint8Array(32))].map(b => b.toString(16).padStart(2, '0')).join('');
    try { sessionStorage.setItem(key, t); } catch {}
  }
  return t;
}
const COMPACT_CONTROLS = '(max-width: 950px) and (pointer: coarse), (max-width: 600px)';
export default function NetplayRoom({ displayName, basePath = '/netplay', keyboardActive = true, onSelectControls, sidebarExtras }) {
  const [room, setRoom] = useState({ members: [], phase: 'empty' }), [me, setMe] = useState(null);
  const [error, setError] = useState(''), [busy, setBusy] = useState(false), [metrics, setMetrics] = useState(null), [focus, setFocus] = useState(false);
  const [gameMode, setGameMode] = useState('shared');
  const [settings, setSettings] = useState(() => !(window.matchMedia?.(COMPACT_CONTROLS).matches ?? window.innerWidth <= 600)), [inputMask, setInputMask] = useState(0);
  const [screenSize, setScreenSize] = useState('fit'), [screenNotice, setScreenNotice] = useState('');
  const [joinConflict, setJoinConflict] = useState(false);
  const socket = useRef(), frame = useRef(), roomRef = useRef(room), meRef = useRef(null), tokenRef = useRef(token(basePath));
  const held = useRef(new Set()), touches = useRef(new Map()), padMask = useRef(0), loaded = useRef(false), panel = useRef();
  const controls = useRef(), focusRef = useRef(focus);
  const settingsRef = useRef(settings); settingsRef.current = settings;
  const fullscreenOwned = useRef(false);
  const acceptJoined = useRef(() => {});
  const settingsId = 'netplay-controls-' + (basePath === '/netplay' ? 'main' : 'side');
  focusRef.current = focus;
  controls.current = { keyboardActive, onSelectControls };
  const post = (type, data = {}) => frame.current?.contentWindow?.postMessage({ tavernNetplay: true, type, data }, window.location.origin);
  const sendHeld = () => {
    let mask = padMask.current; held.current.forEach(b => { mask |= 1 << b; }); touches.current.forEach(value => { mask |= value; }); setInputMask(mask); post('held', { mask });
  };
  const canControl = () => roomRef.current.phase === 'running' && !roomRef.current.hostPaused && Boolean(roomRef.current.members.find(p => p.id === meRef.current)?.seat);
  const releaseKeys = () => { held.current.clear(); padMask.current = 0; sendHeld(); };
  const releaseAll = () => { touches.current.clear(); releaseKeys(); };
  const activateAudio = () => { try { frame.current?.contentWindow?.tavernActivateAudio?.(); } catch {} };
  const exitPlay = () => {
    releaseAll(); setSettings(false); setFocus(false); setScreenNotice('');
    if ((document.fullscreenElement || document.webkitFullscreenElement) === panel.current) {
      try { const result = (document.exitFullscreen || document.webkitExitFullscreen)?.call(document); result?.catch?.(() => {}); } catch {}
    }
  };
  const enterPlay = mode => {
    onSelectControls?.(); activateAudio(); setSettings(false); setScreenSize('fit');
    // Only one lane is expanded; both mounted emulators/sockets keep running.
    window.dispatchEvent(new CustomEvent('tavern-mobile-play', { detail: basePath }));
    setFocus(mode);
  };
  const fullscreen = async () => {
    // Parent-only fullscreen: never replace/reload the iframe or contact the room.
    enterPlay('full'); setScreenNotice('');
    const request = panel.current?.requestFullscreen || panel.current?.webkitRequestFullscreen;
    if (!request) return setScreenNotice('Browser fullscreen is unavailable. The game is maximized on this page.');
    try { await request.call(panel.current); }
    catch { setScreenNotice('Browser fullscreen was blocked. The game is maximized on this page.'); }
  };
  useEffect(() => {
    const media = window.matchMedia?.(COMPACT_CONTROLS);
    const change = event => setSettings(!event.matches);
    media?.addEventListener?.('change', change);
    return () => media?.removeEventListener?.('change', change);
  }, []);
  useEffect(() => {
    const change = () => {
      if ((document.fullscreenElement || document.webkitFullscreenElement) === panel.current) fullscreenOwned.current = true;
      else if (fullscreenOwned.current) { fullscreenOwned.current = false; releaseAll(); setSettings(false); setFocus(false); }
    };
    document.addEventListener('fullscreenchange', change); document.addEventListener('webkitfullscreenchange', change);
    return () => { document.removeEventListener('fullscreenchange', change); document.removeEventListener('webkitfullscreenchange', change); };
  }, []);
  useEffect(() => {
    const select = e => { if (e.detail !== basePath && focusRef.current) exitPlay(); };
    const escape = e => {
      if (e.key !== 'Escape') return;
      // Settings receives focus without remounting the emulator. Escape closes it.
      if (settingsRef.current) { setSettings(false); return; }
      if (focusRef.current) exitPlay();
    };
    window.addEventListener('tavern-mobile-play', select); window.addEventListener('keydown', escape);
    return () => { window.removeEventListener('tavern-mobile-play', select); window.removeEventListener('keydown', escape); };
  }, [basePath]);
  useEffect(() => {
    if (!focus) return;
    return lockPlayViewport();
  }, [focus]);
  const press = (b, value) => {
    if (!Number.isInteger(b) || b < 0 || b > 11 || (value && !canControl())) return;
    value ? held.current.add(b) : held.current.delete(b); sendHeld();
  };
  const init = () => {
    if (!loaded.current || !roomRef.current.game || !meRef.current) return;
    const m = roomRef.current.members.find(p => p.id === meRef.current);
    post('init', { epoch: roomRef.current.epoch, game: { ...roomRef.current.game, romUrl: SERVER + roomRef.current.game.romUrl }, host: roomRef.current.hostId === meRef.current, seat: m?.seat });
  };
  useEffect(() => {
    const s = io(SERVER + basePath, { path: '/socket.io', transports: ['websocket', 'polling'], reconnection: true });
    socket.current = s;
    let unbindFrameControls = () => {};
    const onRoom = r => {
      const previousSeat = roomRef.current.members.find(p => p.id === meRef.current)?.seat;
      roomRef.current = r; setRoom(r);
      const m = r.members.find(p => p.id === meRef.current);
      if (previousSeat !== m?.seat || r.phase !== 'running' || r.hostPaused) releaseAll();
      post('config', { epoch: r.epoch, host: r.hostId === meRef.current, seat: m?.seat });
    };
    const onJoined = result => {
      if (s !== socket.current || !s.connected) return;
      if (result.error) { setJoinConflict(result.error === 'This browser session is already open in another tab'); return setError(result.error); }
      setJoinConflict(false); setError(''); meRef.current = result.id; setMe(result.id); onRoom(result.room); init();
    };
    acceptJoined.current = onJoined;
    s.on('connect', () => {
      setError(''); setJoinConflict(false);
      s.emit('np_join', { token: tokenRef.current, name: displayName, protocol: 6 }, onJoined);
    });
    s.on('np_room', onRoom);
    for (const event of ['np_pause', 'np_hold', 'np_snapshot_request', 'np_snapshot', 'np_begin', 'np_frame']) s.on(event, data => post(event, data));
    s.on('np_pause', () => { setMetrics(null); releaseAll(); });
    s.on('np_hold', releaseAll);
    s.on('np_begin', () => setError(''));
    s.on('np_error', message => setError(String(message)));
    s.on('disconnect', () => { releaseAll(); post('np_pause', { epoch: -1, reason: 'Disconnected; reconnecting…' }); setError('Disconnected; reconnecting…'); });
    s.on('connect_error', () => setError('Cannot connect to local netplay. Retry or use Streaming.'));
    const onMessage = event => {
      if (event.source !== frame.current?.contentWindow || event.origin !== window.location.origin || !event.data?.tavernNetplay) return;
      const { type, data } = event.data;
      if (type === 'frame_loaded') {
        loaded.current = true;
        unbindFrameControls();
        const w = frame.current.contentWindow, select = () => controls.current.onSelectControls?.();
        // Iframe pointer/focus events do not bubble into the parent document.
        w.addEventListener('pointerdown', select, true); w.addEventListener('focus', select); w.addEventListener('blur', releaseKeys);
        unbindFrameControls = () => { w.removeEventListener('pointerdown', select, true); w.removeEventListener('focus', select); w.removeEventListener('blur', releaseKeys); };
        init();
      }
      else if (type === 'status') setMetrics(data);
      else if (type === 'key_input' && controls.current.keyboardActive && data) press(data.button, data.value);
      else if (['np_ready', 'np_wait', 'np_runtime', 'np_resync', 'np_snapshot', 'np_boot', 'np_input', 'np_check', 'np_error'].includes(type)) s.emit(type, data);
    };
    window.addEventListener('message', onMessage);
    return () => { unbindFrameControls(); releaseAll(); window.removeEventListener('message', onMessage); s.disconnect(); loaded.current = false; };
    // Each mount owns one namespace socket. Room updates must not replace it or the iframe.
  }, [displayName, basePath]);
  useEffect(() => {
    const activate = activateAudio;
    for (const event of ['pointerdown', 'touchend', 'click', 'keydown']) window.addEventListener(event, activate, { capture: true, passive: true });
    return () => { for (const event of ['pointerdown', 'touchend', 'click', 'keydown']) window.removeEventListener(event, activate, { capture: true }); };
  }, []);
  useEffect(() => {
    const key = value => event => {
      const b = KEYS[event.code]; if (b === undefined) return;
      // Keyup must release a held key even if focus has moved into chat/an input.
      if (!value) { if (held.current.has(b)) press(b, false); return; }
      if (!keyboardActive || !canControl() || /^(INPUT|TEXTAREA|SELECT)$/.test(event.target.tagName) || event.target.isContentEditable || event.target.closest?.('[contenteditable="true"]')) return;
      if (event.code === 'Enter' && event.target.closest?.('button,a')) return;
      if (event.repeat && !held.current.has(b)) return;
      event.preventDefault(); press(b, true);
    };
    const down = key(true), up = key(false);
    window.addEventListener('keydown', down); window.addEventListener('keyup', up);
    let raf;
    const poll = () => {
      let mask = 0;
      if (keyboardActive && canControl() && document.visibilityState === 'visible' && document.hasFocus()) {
        const p = Array.from(navigator.getGamepads?.() || []).find(p => p?.connected);
        if (p) {
          const map = { 0: 0, 1: 8, 2: 1, 3: 9, 4: 10, 5: 11, 8: 2, 9: 3, 12: 4, 13: 5, 14: 6, 15: 7 };
          Object.entries(map).forEach(([i, b]) => { if (p.buttons[i]?.pressed) mask |= 1 << b; });
          if (p.axes[0] < -0.35) mask |= 1 << 6; if (p.axes[0] > 0.35) mask |= 1 << 7;
          if (p.axes[1] < -0.35) mask |= 1 << 4; if (p.axes[1] > 0.35) mask |= 1 << 5;
        }
      }
      if (mask !== padMask.current) { padMask.current = mask; sendHeld(); }
      raf = requestAnimationFrame(poll);
    };
    raf = requestAnimationFrame(poll);
    // Changing keyboard/gamepad ownership must not release either lane's touch holds.
    return () => { cancelAnimationFrame(raf); releaseKeys(); window.removeEventListener('keydown', down); window.removeEventListener('keyup', up); };
  }, [keyboardActive]);
  useEffect(() => {
    let pauseTimer;
    const blur = () => {
      releaseAll(); clearTimeout(pauseTimer);
      pauseTimer = setTimeout(() => {
        // Focusing a same-origin emulator blurs window but not the whole document.
        if (document.hasFocus()) return;
        const r = roomRef.current;
        if (socket.current?.connected && meRef.current === r.hostId && r.game && !r.hostPaused && ['running', 'loading', 'syncing'].includes(r.phase)) {
          socket.current.emit('np_action', { action: 'pause' });
        }
      }, 100);
    };
    const focusWindow = () => { clearTimeout(pauseTimer); };
    window.addEventListener('blur', blur); window.addEventListener('focus', focusWindow);
    window.addEventListener('pagehide', releaseAll);
    document.addEventListener('visibilitychange', releaseAll);
    // Returning focus never resumes an intentionally or automatically paused game.
    return () => { clearTimeout(pauseTimer); window.removeEventListener('blur', blur); window.removeEventListener('focus', focusWindow); window.removeEventListener('pagehide', releaseAll); document.removeEventListener('visibilitychange', releaseAll); };
  }, []);
  const isHost = me && me === room.hostId, myMember = room.members.find(p => p.id === me), mySeat = myMember?.seat;
  const linked = room.game?.mode === 'linked';
  const assignedControllers = controllerAssignments(room);
  const action = (action, data = {}) => socket.current?.emit('np_action', { action, ...data });
  const requestHostOverride = (pin, done) => {
    const s = socket.current;
    if (!s?.connected) return done({ code: 'UNAVAILABLE' });
    const requestedAt = Date.now();
    const send = () => {
      if (s !== socket.current || !s.connected || Date.now() - requestedAt >= 10000) return done({ code: 'UNAVAILABLE' });
      s.emit('np_host_override', { pin }, done);
    };
    if (!joinConflict) return send();
    // Only an explicit override submission rotates a cloned tab's credential.
    // The original socket is never replaced, disconnected or impersonated.
    tokenRef.current = token(basePath, true);
    s.emit('np_join', { token: tokenRef.current, name: displayName, protocol: 6 }, result => {
      if (s !== socket.current || !s.connected) return done({ code: 'UNAVAILABLE' });
      if (result.error) return done({ code: 'JOIN_FAILED' });
      acceptJoined.current(result); send();
    });
  };
  async function upload(file, state = false) {
    if (!file) return;
    setBusy(true); setError('');
    try {
      const response = await fetch(SERVER + basePath + (state ? '/save' : '/rom'), { method: 'POST',
        headers: { 'Content-Type': 'application/octet-stream', Authorization: 'Bearer ' + tokenRef.current, 'X-Game-Name': file.name, 'X-Game-Mode': gameMode },
        body: await file.arrayBuffer() });
      const result = await response.json(); if (!response.ok) throw new Error(result.error || 'Upload failed');
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  }
  async function download() {
    try {
      const response = await fetch(SERVER + basePath + '/save', { headers: { Authorization: 'Bearer ' + tokenRef.current } });
      if (!response.ok) throw new Error('Save a state first');
      const url = URL.createObjectURL(await response.blob()), a = document.createElement('a');
      a.href = url; a.download = 'tavern-game-' + (basePath === '/netplay' ? '1' : '2') + '-' + (room.game?.system || 'game') + '.state'; a.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) { setError(err.message); }
  }
  return <section ref={panel} className={'netplay-room netplay-room--left-controls' + (focus ? ' netplay-room--play' : '') + (!settings ? ' netplay-room--controls-closed' : '')} data-mobile-play={focus === 'mobile'} data-screen-size={screenSize} onPointerDown={onSelectControls}
    style={{ boxShadow: onSelectControls && keyboardActive ? 'inset 0 0 0 2px #c8922a' : undefined }}>
    <aside className="netplay-sidebar" aria-label={'Game ' + (basePath === '/netplay' ? '1' : '2') + ' controls'}>
    <div className="netplay-mobile-toolbar">
      <strong>Game {basePath === '/netplay' ? '1' : '2'} · {mySeat ? 'P' + mySeat : 'Spectator'}</strong>
      <button style={B} onClick={activateAudio} aria-label="Enable audio">Audio</button>
      {!focus && <button style={B} disabled={!room.game} onClick={() => { setScreenNotice(''); enterPlay('full'); }}>Maximize page</button>}
      {!focus && <button style={B} disabled={!room.game} onClick={() => enterPlay('mobile')}>Mobile Play</button>}
      <button style={B} disabled={!room.game} onClick={fullscreen}>Full screen</button>
      {isHost && room.game && <button style={B} disabled={!room.hostPaused && !['running', 'loading', 'syncing'].includes(room.phase)} onClick={() => action(room.hostPaused ? 'resume' : 'pause')}>{room.hostPaused ? 'Resume game' : 'Pause game'}</button>}
      <button style={B} onClick={() => { releaseAll(); setSettings(!settings); }} aria-expanded={settings} aria-controls={settingsId}>Settings</button>
      {((me && !isHost) || joinConflict) && <HostOverride request={requestHostOverride} disabled={!socket.current?.connected || busy} cloneConflict={joinConflict}/>}
      {focus && <button style={B} aria-label={focus === 'mobile' ? 'Exit Mobile Play' : 'Exit full screen'} onClick={exitPlay}>Exit</button>}
    </div>
    <div id={settingsId} className={'netplay-settings' + (settings ? ' netplay-settings--open' : '')}>
    <button className="netplay-settings-close" style={B} onClick={() => setSettings(false)}>Close settings</button>
    <h2 style={{ margin: '4px 0 8px', fontSize: 18 }}>{basePath === '/netplay' ? 'Local netplay' : 'Game 2 — local netplay'}</h2>
    {sidebarExtras && <div className="netplay-live-options">{sidebarExtras}</div>}
    <fieldset className="netplay-screen-options"><legend>Screen</legend>
      <label>Size <select aria-label="Screen size" style={B} value={screenSize} onChange={e => setScreenSize(e.target.value)}>
        <option value="fit">Fit available space</option><option value="small">Small · up to 360 px</option><option value="medium">Medium · up to 640 px</option>
      </select></label>
      <WakeLockButton />
    </fieldset>
    {onSelectControls && <button style={B} onClick={onSelectControls} aria-pressed={keyboardActive}>{keyboardActive ? 'Keyboard/gamepad active here' : 'Use keyboard/gamepad here'}</button>}
    <div style={{ fontSize: 12, color: '#bbb', marginBottom: 10 }}>Every browser renders locally. Two-frame input buffer; no compressed video. Sound starts automatically when permitted; if blocked, tap the game or any control to start it. Keep player tabs visible. {mySeat ? 'You control P' + mySeat + '.' : 'You are watching.'}</div>
    <div className="netplay-game-actions">
      {isHost && <label style={{ minWidth: 0, maxWidth: '100%' }}>Game mode <select aria-label="Game mode" style={B} value={gameMode} onChange={event => setGameMode(event.target.value)} disabled={busy}><option value="shared">Shared console</option><option value="linked">GB/GBC link cable — own screen</option></select></label>}
      {isHost && <label style={B}>{busy ? 'Uploading…' : 'Upload ROM'}<input className="netplay-file-input" aria-label="Upload ROM" type="file" accept=".nes,.sfc,.smc,.swc,.gen,.md,.bin,.smd,.gb,.gbc,.dmg,.gba,.zip" disabled={busy} onChange={e => { upload(e.target.files?.[0]); e.target.value = ''; }}/></label>}
      {isHost && room.game && <>
        <button style={B} onClick={() => action('refresh')}>Refresh room</button>
        {!linked && <>
          <button style={B} disabled={busy || !['running', 'paused'].includes(room.phase)} onClick={() => action('save')}>Save state</button>
          <button style={B} disabled={!room.hasSave} onClick={() => action('load')}>Load state</button>
          <button style={B} disabled={!room.hasSave} onClick={download}>Download state</button>
        </>}
        <label style={B}>Load state file<input className="netplay-file-input" type="file" aria-label="Load state file" accept=".state" disabled={busy} onChange={e => { upload(e.target.files?.[0], true); e.target.value = ''; }}/></label>
        <button style={B} onClick={() => action('stop')}>Stop game</button>
      </>}
    </div>
    {(linked || gameMode === 'linked') && <div style={{ fontSize: 12, marginBottom: 10 }}>Linked play shows only your assigned {seatDescription(room.game)} console. The host assigns seats and manages saves. Named saves capture and restore {playerSeats(room.game).length === 2 ? 'both' : 'all'} linked consoles together, not just the visible screen. GBA / Four Swords link play is not enabled.</div>}
    {room.game && ['gb', 'gbc'].includes(room.game.system) && <div style={{ fontSize: 12, marginBottom: 10 }}>GB and GBC cartridges run on Game Boy Color hardware. Original GB games use the default color palette.</div>}
    <div className="netplay-seat-options">
      <small aria-live="polite">{assignedControllers.count} controller{assignedControllers.count === 1 ? '' : 's'} assigned</small>
      {assignedControllers.visibleSeats.map(seat =>
      <label key={seat} style={{ minWidth: 0, maxWidth: '100%' }}>Player {seat} <select aria-label={'Player ' + seat} disabled={!isHost} value={room.members.find(p => p.seat === seat)?.id || ''} style={{ ...B, padding: 5 }} onChange={e => action('seat', { seat, memberId: e.target.value || null })}>
        <option value="">Unassigned</option>{room.members.map(m => <option key={m.id} value={m.id}>{m.name}{m.id === room.hostId ? ' (host)' : ''}</option>)}
      </select></label>)}
      {isHost && assignedControllers.count > 0 && assignedControllers.nextSeat !== null &&
        <label style={{ minWidth: 0, maxWidth: '100%' }}>Add controller <select aria-label="Add controller" style={{ ...B, padding: 5 }}
          value="" disabled={!assignedControllers.availableMembers.length}
          onChange={e => { if (e.target.value) action('seat', { seat: assignedControllers.nextSeat, memberId: e.target.value }); }}>
          <option value="">{assignedControllers.availableMembers.length ? 'Assign another user…' : 'Waiting for another user…'}</option>
          {assignedControllers.availableMembers.map(m => <option key={m.id} value={m.id}>{m.name}{m.id === room.hostId ? ' (host)' : ''}</option>)}
        </select></label>}
    </div>
    {isHost && room.game && ['gb', 'gbc'].includes(room.game.system) && <NetplayNamedSavesPanel key={room.game.id} basePath={SERVER + basePath} sessionToken={tokenRef.current} socket={socket.current} game={room.game} phase={room.phase} disabled={busy}/>}
    </div>
    </aside>
    <div className="netplay-content">
    {!mySeat && <div className="netplay-seat-hint netplay-seat-hint--spectator" role="status">{linked ? 'You are watching Player 1, not controlling.' : 'You are watching, not controlling.'} {isHost ? 'Use Settings to assign yourself a player seat.' : 'Ask the host to assign your name to ' + seatDescription(room.game) + ' in Settings. Tapping Mobile Play does not claim a seat.'} {linked && 'Once assigned, you see your own console. The host manages named whole-room saves.'}</div>}
    {mySeat && !focus && <div className="netplay-seat-hint">You control P{mySeat}. Hold the pad and buttons together; slide the pad for diagonals. {room.hostPaused && 'Paused by host — the host must press Resume game.'}</div>}
    {error && <div className="netplay-alert" role="alert" style={{ color: '#ff9988', padding: 8 }}>{error}</div>}
    {screenNotice && <div className="netplay-screen-notice" role="status">{screenNotice}</div>}
    <div className="netplay-status" data-testid="netplay-status" style={{ fontSize: 12, color: room.phase === 'running' ? '#6f6' : '#ccc', marginBottom: 8 }}>
      {room.hostPaused ? 'Paused by host' : myMember?.joining ? 'Joining in background' : room.phase} · {room.game?.name || 'The first guest is host. Upload NES, SNES, Genesis, GB, GBC or GBA.'}
      {metrics && ' · ' + Math.round(metrics.fps) + ' FPS · frame ' + metrics.frame + ' · audio ' + metrics.audio}
    </div>
    {room.game && room.phase !== 'running' && room.reason && <div className="netplay-reason" role="status" style={{ fontSize: 12, marginBottom: 8 }}>{room.hostPaused ? 'The host must press Resume game to continue. ' : ''}{room.reason}</div>}
    {room.game && <div className="netplay-screen" style={{ '--netplay-aspect': personalScreenAspect(room.game) }}><div className="netplay-display">
      <iframe key={room.game.id} ref={frame} onFocus={onSelectControls} title={'Game ' + (basePath === '/netplay' ? '1' : '2') + ' — local ' + (room.game.profile?.label || 'SNES') + ' emulator'} src={(SERVER || '') + '/netplay-player.html?game=' + room.game.id} allow="autoplay; fullscreen" style={{ width: '100%', height: '100%', display: 'block', border: 0 }}/>
    </div></div>}
    {room.game && mySeat && <MobileControls system={room.game.system} seat={mySeat} disabled={room.phase !== 'running' || room.hostPaused}
      touches={touches} sendHeld={sendHeld} canControl={canControl} mask={inputMask}/>}
    <div className="netplay-footer" style={{ fontSize: 11, color: '#999' }}>Keyboard: arrows, Z/X/A/S, Enter, Shift, Q/W. Physical gamepads supported. Leaving the browser pauses games you host; use Resume game to continue. {room.game && ['gb', 'gbc'].includes(room.game.system) ? 'The host manages durable named saves in Settings. Save state is a temporary session snapshot; named saves persist.' : 'Session snapshots remain until the game/service stops; download a state to keep it.'}</div>
    </div>
  </section>;
}
